import {useState} from "react";
import {Redirect} from "react-router-dom";
import {api} from "../../lib/api";
import routes from "../../routes";
import PublicLayout from "../../layouts/public-layout";
import {rules, SubmitButton, XForm} from "@cdx/common";
import {useAuthFlow} from "../../features/auth/oauth-flow";
import {Box, Col, DSButton, DSIconButton, DSIconDiscord, DSIconGoogle, Row} from "@cdx/ds";
import {WrappedDSInput} from "@cdx/common/forms/WrappedDSInput";
import cdxEnv from "../../env";

const formRules = (vals) => ({
  email: [rules.isRequired],
  password: vals.resetPassword ? [] : [rules.isRequired],
  resetPassword: [],
});

const AuthButton = ({provider, children, onLogin, icon}) => {
  const buttonProps = useAuthFlow({
    provider,
    onDone: (userId) =>
      userId ? onLogin : Promise.reject("No Codecks user was found with the provided auth method."),
    intent: "login",
  });
  return <DSIconButton variant="secondary" label={children} icon={icon} {...buttonProps} />;
};

const AuthProviders = ({onLogin}) => (
  <Row sp="16px" align="center" justify="center">
    <Box bold size={16} color="primary">
      Sign in with
    </Box>
    <Row sp="16px">
      <AuthButton provider="discord" onLogin={onLogin} icon={<DSIconDiscord />}>
        Discord
      </AuthButton>
      <AuthButton provider="google" onLogin={onLogin} icon={<DSIconGoogle />}>
        Google
      </AuthButton>
    </Row>
  </Row>
);

const FormSubmitButton = (props) => (
  <Col align="center">
    <SubmitButton as={DSButton} size="lg" {...props} />
  </Col>
);

export const LoginForm = ({onLogin}) => {
  const [values, setValues] = useState({email: "", password: "", resetPassword: false});
  const [instructionTarget, setInstructionTarget] = useState(null);

  const handleLoginSubmit = ({email, password}) => {
    return api.mutate.users.login({email, password}).then(
      () => {
        api.cache.clear({onDone: onLogin});
      },
      (res) => {
        const msgs = {
          rate_limit: "Too many log in attempts. Please wait 5 seconds.",
          no_user: {email: "There's no user with this email."},
          bad_password: {password: "Wrong password."},
          no_password: {
            password:
              "You haven't set up a password yet. Please use the 'forgot password' feature to set one up.",
          },
          only_discord: {
            password:
              "You've authenticated via Discord. Please use the button above. You can also use the 'forgot password' flow to set a password for your account.",
          },
          only_google: {
            password:
              "You've authenticated via Google. Please use the button above. You can also use the 'forgot password' flow to set a password for your account.",
          },
        };
        return Promise.reject(msgs[res] || res);
      }
    );
  };

  const handlePasswordReset = ({email}) =>
    api.mutate.users
      .requestPasswordReset({email, isPublic: process.env.REACT_APP_MODE === "open"})
      .then(
        () => setInstructionTarget(email),
        (res) => {
          const msgs = {no_user: {email: "There's no user with this email."}};
          return Promise.reject(msgs[res] || res);
        }
      );

  return (
    <Col sp="32px">
      {cdxEnv.ON_PREMISE !== "true" && <AuthProviders onLogin={onLogin} />}
      <Col styleChild sp="16px" pa="24px" borderWidth={1} borderColor="default" rounded={4}>
        <XForm
          values={values}
          onChange={setValues}
          rules={formRules}
          onSubmit={values.resetPassword ? handlePasswordReset : handleLoginSubmit}
          buttonComp={FormSubmitButton}
          buttonProps={{children: values.resetPassword ? "Request password reset" : "Login"}}
        >
          {cdxEnv.ON_PREMISE !== "true" && (
            <Box size={16} bold textAlign="center" color="primary">
              ... or use your email address
            </Box>
          )}
          <XForm.Field
            as={WrappedDSInput}
            name="email"
            placeholder="Your email"
            label="Email"
            autoFocus
          />
          {!values.resetPassword && (
            <XForm.Field
              as={WrappedDSInput}
              type="password"
              name="password"
              label="Password"
              placeholder="Your password"
              autoComplete="current-password"
            />
          )}
          <Col align="center" colorTheme="purpleTextOnLight">
            <DSButton
              variant="tertiary"
              onClick={() => setValues((prev) => ({...prev, resetPassword: !prev.resetPassword}))}
              active={values.resetPassword}
              size="sm"
            >
              {values.resetPassword ? "Back" : "Forgot your password?"}
            </DSButton>
          </Col>
        </XForm>
      </Col>

      {instructionTarget && (
        <Box
          colorTheme="active50"
          bg="foreground"
          color="primary"
          size={16}
          px="16px"
          py="16px"
          rounded={4}
          textAlign="center"
        >
          Reset instructions sent to <b>{instructionTarget}</b>
        </Box>
      )}
    </Col>
  );
};

const Login = ({location, root}) => {
  const [redirect, setRedirect] = useState(false);
  const handleLogin = () => {
    setRedirect(true);
  };

  const isLoggedIn = root.loggedInUser && root.loggedInUser.$meta.isLoaded;

  if (isLoggedIn || redirect) {
    const {from} = location.state || {from: {pathname: routes.hand.getUrl()}};
    return <Redirect to={from} />;
  }

  return (
    <PublicLayout width="xxs" sp={6}>
      <Box as="h1" color="primary" textAlign="center" bold size={24}>
        {root.account.name} Login
      </Box>
      <LoginForm onLogin={handleLogin} />
    </PublicLayout>
  );
};

export default Login;
